/** `MouseEvent.detail` du 1er clic d'une séquence (2 = double clic, 3 = triple…). */
const FIRST_CLICK_RANK = 1;

/**
 * Vrai si cet événement souris (`mousedown`, `mouseup`, `click`) est la suite
 * d'une rafale multi-clics (double, triple clic…) démarrée par un clic
 * précédent : le navigateur incrémente `event.detail` quand deux clics sont
 * proches dans le temps et l'espace, quelle que soit la cible.
 *
 * Cas d'usage : un overlay (modale, backdrop) ouvert par le 1er clic reçoit le
 * 2ᵉ « à la place » du bouton qui l'a ouvert. Ce 2ᵉ événement n'exprime pas
 * l'intention de cliquer sur l'overlay — un handler « clic à l'extérieur »
 * doit l'ignorer, sans recourir à un délai arbitraire.
 */
export function isRepeatedClick(event: MouseEvent): boolean {
  return event.detail > FIRST_CLICK_RANK;
}

/**
 * Vrai si ce `click` vient du CLAVIER et non d'un pointeur.
 *
 * Entrée ou Espace sur un élément natif (`<button>`, `<a>`) font émettre au
 * navigateur un `click` synthétique, dont `detail` vaut 0 : il ne compte aucun
 * clic, parce qu'il n'y en a pas eu. Un vrai clic de souris porte toujours au
 * moins 1. `element.click()` appelé depuis du code vaut 0 lui aussi — c'est la
 * même absence de pointeur, et c'est pourquoi un test qui simule un clic de
 * SOURIS doit poser `detail: 1` explicitement.
 *
 * Cas d'usage : un contrôle dont le chemin souris est DÉJÀ pris par autre
 * chose. La poignée de réordonnancement en est l'exemple : poignée de
 * glisser-déposer pour la souris, bouton « saisir au clavier » pour le
 * clavier. Sans ce tri, un simple clic sur la poignée ferait entrer en mode
 * déplacement clavier l'utilisateur qui voulait seulement glisser — un
 * comportement à la souris qui n'existait pas avant.
 *
 * À n'employer que là où les deux chemins divergent VRAIMENT. Partout
 * ailleurs, un `(click)` unique est la bonne réponse : il couvre les deux, et
 * c'est tout l'intérêt d'un élément natif.
 */
export function isKeyboardActivation(event: MouseEvent): boolean {
  return event.detail === 0;
}
